<template>
  <DialogRoot :open="showChatPopup" :modal="false" @update:open="closeIframe" v-if="isMyComponentEnabled">
    <DialogPortal>
      <div class="overscroll-none">
      <DialogContent
            class="chatPopup text-gray-900 fixed z-[9999]
            min-w-[325px] w-full right-[1.7rem] sm:w-[375px]
            left-1/2 -translate-x-1/2
            sm:left-auto 
            sm:translate-x-0 sm:translate-y-0
            md:left-auto sm:translate-x-0
            rounded-tl-[13px] rounded-tr-[13px] border-gray-300  transition-all duration-300 ease-in-out"
            :class="{
              // Handle height adjustments based on state (minimized vs expanded)
              'h-[48px]': minimiseValue,  // Minimized height on mobile
              // For large and extra-large devices
              'h-[674px]': breakPoints.between('lg', 'xl') && !minimiseValue,
              'h-[60dvh]': !minimiseValue && !isMobileBreakpoint, // Default fixed height for larger screens
              'desktop' : !isMobileBreakpoint, // Add a class for desktop view
              'minimized' : minimiseValue // Add a class for minimized state
            }"
            :style="{ height: minimiseValue ? (isMobileBreakpoint ? '6dvh' : '48px') : chatHeight }"
            @interact-outside="event => event.preventDefault()"
          >
          <iframe
              scrolling="no"
              class="w-full h-full"
              @load="sendMessageToIframe"
              ref="iframeRef"
              :src="iframeSrc"
              title="chatbot-iframe"
              allow="clipboard-write"
          ></iframe>
      </DialogContent>
      </div>
    </DialogPortal>
  </DialogRoot>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, computed } from 'vue';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useIframeStore } from '@/stores/chatAIStore';
import { useUserStore } from '@/stores/userStore';
import { useToastStore } from '@/stores/toastStore';
import debounce from 'debounce';
import { storeToRefs } from 'pinia';
import { useAddToCartToastStore } from '~/stores/addToCartToastStore'

import { useDynamicYieldStore } from '@/stores/dynamicYieldStore';
import {
  DialogContent,
  DialogPortal,
  DialogRoot,
} from 'radix-vue';

import { useSessionStore } from '@/stores/sessionStore';
const addToCartToast = useAddToCartToastStore()

const dyStore = useDynamicYieldStore();
const sessionStore = useSessionStore();

  const isMyComponentEnabled = computed(() => {
      const myVariationData = dyStore.getCustomJSONVariationBySelector(config?.public?.abTestName ?? '');
      return !!myVariationData?.data?.enabled;
  });
const nuxtApp = useNuxtApp();
const config = nuxtApp.$config;
const router = useRouter();
const userStore = useUserStore();
const toastStore = useToastStore();
const hideAIChatPopUpCookie = useCookie('hide-AI-Chat');

const breakPoints = useBreakpoints(breakpointsTailwind);
const isMobileBreakpoint = breakPoints.smaller('md');

const iframeMessages = ref<string[]>([]);
const iframeStore = useIframeStore();
const { inputSearchQuery ,minimiseValue} = storeToRefs(iframeStore);
const showChatPopup = computed(() => iframeStore.showChatPopup);
const iframeRef = ref<HTMLIFrameElement | null>(null);
const cartQuantity = ref('0');
const isMobileMax = ref(false);
const { isBannerActive } = defineProps<{
  isBannerActive?: boolean
}>()
const chatHeight = computed(() => {
  if (iframeStore.initIframe && isMobileMax.value) {
    return isBannerActive ? '55dvh' : '92dvh';
  }

  if (isMobileBreakpoint.value) {
    if (isBannerActive) {
      return `calc(91.5dvh - ${iframeStore.annonceMentHeight}px)`;
    }
    return '92dvh';
  }

  if (isBannerActive) {
    return `calc(85dvh - ${iframeStore.annonceMentHeight}px)`;
  }

  return '85dvh';
});


if (process.client) {
  const storedQuantity = sessionStorage.getItem('cartQuantity');
  if (storedQuantity) {
    cartQuantity.value = storedQuantity;
  }
}

const iframeSrc = computed(() => {
  return `${config.public.chatIframeDomain}?cartQuantity=${cartQuantity.value}`;
});

const sessionId = computed(() => sessionStore.sessionId);
const closeIframe = () => {
  iframeStore.closeIframe();
  iframeStore.setIframeLoaded(false);
};

const closeDialog = () => {
  iframeStore.closeIframe();
  iframeStore.setIframeLoaded(false);
};


const sendMessageToIframe = debounce((e: Event) => {
  if (iframeRef.value && iframeRef.value.contentWindow) {
      iframeStore.setIframeRef(iframeRef.value);
      iframeStore.setIframeLoaded(true);
      const iframeWindow = iframeRef.value.contentWindow;
      let message =  inputSearchQuery.value || '';  
      const iframeDomain = config.public.chatIframeDomain;
      const cookies = document.cookie; 

      const allCookies = cookies.split(';').reduce((acc, cookie) => {
      const [key, value] = cookie.split('=').map(s => s.trim());
      acc[key] = value;
      return acc;
    }, {});
          iframeRef.value.contentWindow.postMessage({
          type: 'set-cookies',
          cookies: allCookies,
          message:message,
          isMobile:isMobileBreakpoint.value,
          sessionId: sessionId.value,
          minimiseValue: minimiseValue.value === true ? false : true,
        }, iframeDomain);
  }
}, 500);

const enableViewCartCta = () => {
   if (iframeRef.value && iframeRef.value.contentWindow) {
    iframeRef.value.contentWindow.postMessage({
          type: 'enable-viewcart-cta'
        }, config.public.chatIframeDomain);
    }
}


const addToCart = (productId: number | undefined) => {
  if (!!productId) {
    siteService()
      .addToCart(productId)
      .then(() => { 
        addToCartToast.open("success", "Item added to cart!");
        enableViewCartCta();
      })
      .catch(() => addToCartToast.open("error", "Failed to add to cart"));
  } else {
      addToCartToast.open("error", "Failed to add to cart");
  }
};

const handleIframeMessage = (event: MessageEvent) => {
  if (event.data.type === 'FROM_IFRAME') {
    iframeMessages.value.push(event.data.message);
  }
  if (event.data.type === 'CLOSE_DIALOG') {
    closeDialog();
  }
  if (event.data.type === 'MIMIZE_MAXMISE') {
    iframeStore.UpdateMiniseAndMaximiseValue();
  }
  if (event.data.type === 'REDIRECT_URL') {
    const url = event.data?.url;
    if (url?.startsWith('http://') || url?.startsWith('https://')) {
      window.location.href = url;
    } else {
      router.push(url).then(() => {
        window.location.reload();
      });

    }
  }
  if (event.data.type === 'ADDTOCART_CLICK') {
    const productId = event.data?.productId;
    if (productId) {
      addToCart(productId);
  }
};
};

onMounted(() => {
  sessionStore.fetchSessionId();
  if (iframeRef.value && iframeRef.value.contentWindow) {
      iframeStore.setIframeRef(iframeRef.value);
      iframeStore.setIframeLoaded(true);
  }
  cartQuantity.value = sessionStorage.getItem('cartQuantity') || '0';
  window.addEventListener('message', handleIframeMessage);

  //chat window Set to 50% of mobile viewport
  const params = new URLSearchParams(window.location.search);
  const isMobileQuery = params.get('isMobile');

  if (isMobileQuery === 'true') {
    isMobileMax.value = true
    iframeStore.minimiseValue = false; 
    iframeStore.openIframe(); // Show the chat popup
  }

});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleIframeMessage);
});

</script>
